import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Loader2 } from 'lucide-react';

const SEV_CHIP = {
  critical: 'bg-rose-600 text-white', high: 'bg-orange-500 text-white',
  medium: 'bg-amber-400 text-amber-950', low: 'bg-slate-300 text-slate-700',
};

const RuleRow = ({ r, active }) => (
  <div id={`rule-${r.rule}`} className={`scroll-mt-24 px-4 py-3 border-b border-slate-100 last:border-0 flex gap-4 transition-colors ${active ? 'bg-amber-50' : ''}`}
    data-testid={`qa-rule-${r.rule}`}>
    <div className="w-20 shrink-0">
      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${SEV_CHIP[r.severity]}`}>{r.severity}</span>
      <div className="text-[10px] text-slate-400 mt-1.5">Rule {r.rule}</div>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-bold text-slate-800">{r.name}</div>
      {r.what && <p className="text-xs text-slate-600 mt-0.5">{r.what}</p>}
      {r.where && <p className="text-[11px] text-slate-400 mt-1">Where to look: {r.where}</p>}
    </div>
  </div>
);

export default function CourseQaRulesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const active = decodeURIComponent((location.hash || '').replace('#rule-', ''));

  useEffect(() => { api.get('/course-qa/rules').then(r => setData(r.data)); }, []);
  useEffect(() => {
    if (data && active) setTimeout(() => document.getElementById(`rule-${active}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  }, [data, active]);

  const navTab = (to, label) => (
    <button key={to} onClick={() => navigate(to)}
      className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${location.pathname === to ? 'text-white' : 'text-slate-300 hover:text-white'}`}
      data-testid={`qa-nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>
  );
  const match = r => !q || `${r.rule} ${r.name} ${r.what} ${r.where}`.toLowerCase().includes(q.toLowerCase());
  const cats = data ? [...new Set(data.rules.map(r => r.category_name))] : [];

  return (
    <div className="-m-4 sm:-m-6" data-testid="course-qa-rules-page">
      <div className="bg-[#0f2b4c] px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
        <span className="text-white text-base sm:text-lg font-bold tracking-tight">Course Page QA</span>
        <div className="flex items-center gap-1 flex-wrap">
          {navTab('/course-qa', 'Audit a page')}
          {navTab('/course-qa/all', 'Audit all courses')}
          {navTab('/course-qa/defects', 'Defect board')}
          {navTab('/course-qa/rules', 'Rules guide')}
          {user?.role !== 'course_qa' && navTab('/course-qa/management', 'Management')}
        </div>
      </div>
      <div className="p-4 sm:p-6 bg-slate-50 min-h-[calc(100vh-7rem)]">
        <div className="space-y-5 max-w-5xl">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Rules guide</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl">
            Every issue in Course QA comes from one of these checks. Rules 1–33 are the signed-off checks run on the course record.
            R1–R4 come from opening the live page in a browser ("Check live page"). X1–X2 compare images across all courses.
          </p>
        </div>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search rules…" className="h-8 w-64 rounded-md text-xs bg-white" data-testid="qa-rules-search" />
        {!data && <div className="text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
        {cats.map(c => {
          const rows = data.rules.filter(r => r.category_name === c && match(r));
          if (!rows.length) return null;
          return (
            <section key={c} data-testid={`qa-rules-cat-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <h2 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{c}</h2>
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                {rows.map(r => <RuleRow key={r.rule} r={r} active={String(r.rule) === active} />)}
              </div>
            </section>
          );
        })}
        {data && (
          <section data-testid="qa-rules-sections">
            <h2 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Page sections and their CMS codes</h2>
            <p className="text-xs text-slate-500 mb-2">The CMS names each page section with a code starting "t_". You'll see the code in small grey text under the section name on issues.</p>
            <div className="bg-white border border-slate-200 rounded-md grid sm:grid-cols-2">
              {data.sections.map(s => (
                <div key={s.code} className="px-4 py-2 border-b border-slate-100 flex justify-between gap-3 text-xs">
                  <span className="text-slate-800">{s.name}</span>
                  <span className="font-mono text-slate-400">{s.code}</span>
                </div>
              ))}
            </div>
          </section>
        )}
        </div>
      </div>
    </div>
  );
}
